import "@xyflow/react/dist/style.css";
import "./custom.css";

import { useRef } from "react";
import { createBrowserRouter, Outlet, RouterProvider } from "react-router-dom";
import { QueryParamProvider } from "use-query-params";
import { ReactRouter6Adapter } from "use-query-params/adapters/react-router-6";

import { ErrorBoundary } from "@/components/errors/ErrorBoundary";
import { UrqlProvider } from "@/components/graphql";
import { GraphqlWsClientProvider } from "@/components/graphql/GraphqlWsContext";
import { LayerThemeProvider } from "@/components/theme";
import { SnackbarProvider } from "@/components/ui-v2/Snackbar";
import { routes } from "@/routes/routes";

import { AppErrorBoundary } from "./components/AppErrorBoundary";
import { AuthProvider } from "./components/AuthProvider";
import { ConfigProvider } from "./components/ConfigProvider";
import { MobileError, useIsMobile } from "./components/mobile";
import { NavigationIndicator } from "./components/NavigationIndicator";
import { SentryProvider } from "./components/SentryProvider";
import { Utm } from "./components/Utm";
import { VersionChecker } from "./components/VersionChecker";

const AppContent = () => {
  const routesRef = useRef(
    createBrowserRouter([
      {
        path: "/",
        errorElement: <AppErrorBoundary />,
        element: (
          <QueryParamProvider adapter={ReactRouter6Adapter}>
            <AuthProvider>
              <GraphqlWsClientProvider>
                <UrqlProvider>
                  <SnackbarProvider>
                    <VersionChecker />
                    <NavigationIndicator />
                    <Utm />
                    <Outlet />
                  </SnackbarProvider>
                </UrqlProvider>
              </GraphqlWsClientProvider>
            </AuthProvider>
          </QueryParamProvider>
        ),
        children: routes,
      },
    ]),
  );

  const isMobile = useIsMobile();

  if (isMobile && !import.meta.env.DEV) {
    return <MobileError />;
  }

  /**
   * Note:
   * Not the typical setup but `createBrowserRouter` must be called after Sentry is initialized.
   * Sentry is initialized dynamically because its dsn is coming from the backend instead of ENV
   */
  return <RouterProvider router={routesRef.current} />;
};

export const App = () => {
  return (
    <LayerThemeProvider>
      <ConfigProvider>
        <SentryProvider>
          <ErrorBoundary showHelpLinks>
            <AppContent />
          </ErrorBoundary>
        </SentryProvider>
      </ConfigProvider>
    </LayerThemeProvider>
  );
};
